import { useEffect } from "react"

import { Keys, Key, Shortcut, ModifierKey } from "./types"

export default function useKeyboardShortcut(
  shortcuts: Shortcut | Shortcut[],
  callback: (event: KeyboardEvent) => void,
  options?: {
    preventDefault?: boolean
    disableOnModalOpen?: boolean
    enableOnFormTags?: boolean
    enabled?: boolean
    capture?: boolean
  }
) {
  const {
    preventDefault = true,
    disableOnModalOpen = true,
    enableOnFormTags = false,
    enabled = true,
    capture = false,
  } = options ?? {}

  useEffect(() => {
    if (!enabled) {
      return
    }

    function listener(e: KeyboardEvent) {
      const isFormTag =
        e.target instanceof HTMLElement &&
        ["INPUT", "SELECT", "TEXTAREA"].includes(e.target.tagName.toUpperCase())
      if (isFormTag && !enableOnFormTags) {
        return
      }
      if (
        isShortcutMatch(e, Keys.ENTER) &&
        e.target instanceof HTMLElement &&
        e.target.closest("button, a[href]")
      ) {
        return
      }

      const normalized = Array.isArray(shortcuts) ? shortcuts : [shortcuts]
      if (!normalized.some((shortcut) => isShortcutMatch(e, shortcut))) {
        return
      }

      // An `action` query param signals an open modal. Read it live from the
      // URL rather than a router hook so this works under both the Pages Router
      // (web) and the App Router (web-cxa).
      if (disableOnModalOpen && isModalOpen()) {
        return
      }

      if (preventDefault) {
        e.preventDefault()
      }
      // In capture phase, stop the event before bubble-phase listeners (e.g.
      // page-level tab switching) can also fire — so an overlay's shortcut wins.
      if (capture) {
        e.stopPropagation()
      }
      callback(e)
    }

    window.addEventListener("keydown", listener, { capture })

    return () => {
      window.removeEventListener("keydown", listener, { capture })
    }
  }, [
    enabled,
    disableOnModalOpen,
    enableOnFormTags,
    shortcuts,
    preventDefault,
    capture,
    callback,
  ])
}

function isModalOpen(): boolean {
  if (typeof window === "undefined") {
    return false
  }
  return new URLSearchParams(window.location.search).has("action")
}

export function isShortcutMatch(e: KeyboardEvent, shortcut: Shortcut): boolean {
  const pressedModifiers: ModifierKey[] = []
  if (e.altKey) pressedModifiers.push(Keys.ALT)
  if (e.metaKey) pressedModifiers.push(Keys.CMD)
  if (e.ctrlKey) pressedModifiers.push(Keys.CTRL)
  if (e.shiftKey) pressedModifiers.push(Keys.SHIFT)

  if (pressedModifiers.length > 1) {
    return false
  }

  if (typeof shortcut === "string") {
    return e.key === shortcut && pressedModifiers.length === 0
  }

  const [modifier, key] = shortcut
  return modifier === pressedModifiers[0] && e.key === key
}

export function cmdOrCtrl(key: Key): Shortcut[] {
  return [
    [Keys.CMD, key],
    [Keys.CTRL, key],
  ]
}
